<template>
  <div
    id="table-pod-item"
    class="bg-surface-primary text-fg-primary cursor-pointer flex justify-start items-center rounded shadow text-sm hover:shadow-podHover transition-all duration-300 ease-in-out overflow-hidden"
    :class="{ 'opacity-50': isDeleted, italic: updateStatus !== TABLE_UPDATE_STATUS.AVAILABLE }"
  >
    <div class="flex items-center justify-start gap-2 px-3 w-1/2">
      <svg
        width="10"
        height="10"
        viewBox="0 0 10 10"
        stroke-width="1"
        class="w-4 h-4 fill-none"
        id="shape"
        :class="statusStrokeClass"
      >
        <circle v-if="shape === TableShape.CIRCLE" id="circle-shape" cx="5" cy="5" r="4" />
        <rect
          v-else-if="shape === TableShape.SQUARE"
          id="square-shape"
          rx="1"
          x="1"
          y="1"
          width="8"
          height="8"
        />
        <path
          v-else-if="shape === TableShape.TRIANGLE"
          id="triangle-shape"
          d="M 1 1 L 1 9 L 9 1 Z"
        />
        <path
          v-else-if="shape === TableShape.HALF_CIRCLE"
          id="half-circle-shape"
          d="M 5 1 A 4 4 0 0 1 5 9 Z"
        />
      </svg>
      <span
        id="table-name"
        class="truncate shrink-[20] tabular-nums"
        :class="isDeleted ? 'line-through' : ''"
      >
        {{ table.name }}
      </span>
    </div>
    <span class="py-2 tabular-nums" id="table-guest-range">
      {{ table.minimum_guests }}-{{ table.maximum_guests }}
    </span>
    <div class="ml-auto flex items-center justify-end gap-0 w-20">
      <div
        v-if="hasUpdates"
        id="restore-button"
        class="px-3 py-2 hover:bg-surface-base transition-all"
        @click.stop="emit('click:resetTable', table.id)"
      >
        <ArrowReturnLeftIcon class="w-3 h-3" />
      </div>
      <div
        v-if="allowToDelete"
        id="delete-button"
        class="px-3 py-2 hover:bg-surface-base hover:text-fg-warning transition-all"
        @click.stop="emit('click:deleteTable', table.id)"
      >
        <component :is="isDeleted ? UnTrashIcon : TrashIcon" class="w-4 h-4" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue'

import ArrowReturnLeftIcon from '@/assets/arrowReturnLeft.svg?component'
import TrashIcon from '@/assets/trash.svg?component'
import UnTrashIcon from '@/assets/unTrash.svg?component'
import {
  TABLE_UPDATE_STATUS,
  TableStatus,
} from '@/modules/performance/interfaces/PerformanceTableInterfaces'

import {
  PerformanceTableId,
  PerformanceTableRead,
  ReservationStatus,
  ReservationStatusType,
  TableShape,
  TableShapeType,
} from '@generated/types'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (event: 'click:deleteTable', tableId: PerformanceTableId): void
  // eslint-disable-next-line no-unused-vars
  (event: 'click:resetTable', tableId: PerformanceTableId): void
}>()

const props = defineProps<{
  shape: TableShapeType
  updateStatus: TableStatus
  reservationStatus: ReservationStatusType | null
  isDeleted: boolean
  table: PerformanceTableRead
  allowToDelete: boolean
  hasUpdates: boolean
}>()
const { reservationStatus } = toRefs(props)

const statusStrokeClass = computed<string>(function () {
  switch (reservationStatus.value) {
    case ReservationStatus.SEATED:
      return 'stroke-status-seated'
    case ReservationStatus.ARRIVED:
      return 'stroke-status-waiting'
    case ReservationStatus.EXPECTED:
      return 'stroke-status-expected'
    case ReservationStatus.NO_SHOW:
      return 'stroke-status-noshow'
    case ReservationStatus.UNPAID:
      return 'stroke-status-noshow'
    case ReservationStatus.CLEARED:
    case ReservationStatus.CANCELED:
      return 'stroke-transparent'
    default:
      return 'stroke-status-available'
  }
})
</script>
